import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import api from "@/lib/api";
import { Loader2, MessageCircle, Sparkles, Archive, Trash2, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

export default function AdminAsk() {
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const { data: convs, isLoading } = useQuery({ queryKey: ["ask-convs", showArchived], queryFn: async () => (await api.get("/admin/ask-vytali/conversations", { params: { include_archived: showArchived } })).data });
  const { data: settings } = useQuery({ queryKey: ["ask-settings"], queryFn: async () => (await api.get("/admin/ask-vytali/settings")).data });

  const archive = async (id, val) => {
    try { await api.post(`/admin/ask-vytali/conversations/${id}/archive`, { archived: val }); toast.success(val ? "Conversation archivée" : "Conversation désarchivée"); qc.invalidateQueries({ queryKey: ["ask-convs"] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer définitivement cette conversation ? Cette action est irréversible.")) return;
    try { await api.delete(`/admin/ask-vytali/conversations/${id}`); toast.success("Conversation supprimée"); qc.invalidateQueries({ queryKey: ["ask-convs"] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (isLoading) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  const list = Array.isArray(convs) ? convs : [];

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight">Ask Vytali — Conversations</h1>
      <div className="mt-3 flex flex-wrap gap-3 text-sm">
        <span className="rounded-full bg-white/5 px-3 py-1.5 text-muted-foreground">Moteur : <b className="text-foreground">{settings?.provider || "emergent-claude"}</b></span>
        <span className="rounded-full bg-white/5 px-3 py-1.5 text-muted-foreground">VytaliLeads : <b className={settings?.vytalileads_connected ? "text-emerald-400" : "text-amber-400"}>{settings?.vytalileads_connected ? "connecté" : "stub (à venir)"}</b></span>
        <span className="rounded-full bg-white/5 px-3 py-1.5 text-muted-foreground">{list.length} conversation(s)</span>
        <button onClick={() => setShowArchived((s) => !s)} data-testid="toggle-archived" className={`rounded-full px-3 py-1.5 transition-colors ${showArchived ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}>
          {showArchived ? "Masquer archivées" : "Afficher archivées"}
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {list.length === 0 && <p className="text-muted-foreground">Aucune conversation pour l'instant.</p>}
        {list.map((c) => (
          <div key={c.id} className="rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-4" data-testid={`ask-conv-${c.id}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {c.offline ? <MessageCircle className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5 text-primary" />}
                {c.offline ? "Message hors ligne" : "Conversation"} · {new Date(c.updated_at).toLocaleString("fr-FR")}
              </span>
              {c.context?.video_title && <span className="max-w-[45%] truncate text-[11px] text-primary">🎬 {c.context.video_title}</span>}
            </div>
            <div className="mb-2 flex items-center gap-1.5">
              {c.archived
                ? <button onClick={() => archive(c.id, false)} data-testid={`unarchive-${c.id}`} className="flex items-center gap-1 rounded-md bg-white/5 px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"><ArchiveRestore className="h-3 w-3" /> Désarchiver</button>
                : <button onClick={() => archive(c.id, true)} data-testid={`archive-${c.id}`} className="flex items-center gap-1 rounded-md bg-white/5 px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"><Archive className="h-3 w-3" /> Archiver</button>}
              <button onClick={() => del(c.id)} data-testid={`delete-conv-${c.id}`} className="flex items-center gap-1 rounded-md bg-white/5 px-2 py-1 text-[11px] text-muted-foreground hover:text-destructive"><Trash2 className="h-3 w-3" /> Supprimer</button>
              {c.archived && <span className="ml-auto text-[10px] uppercase tracking-widest text-amber-400">Archivée</span>}
            </div>
            {c.offline ? (
              <p className="text-sm"><b>{c.name || "—"}</b> · {c.email}<br /><span className="text-muted-foreground">{c.message}</span></p>
            ) : (
              <div className="max-h-44 space-y-1.5 overflow-y-auto">
                {(c.messages || []).slice(-6).map((m, i) => (
                  <p key={i} className={`text-xs ${m.role === "user" ? "text-foreground" : "text-muted-foreground"}`}>
                    <b>{m.role === "user" ? "Visiteur" : "Vytali"} :</b> {m.content}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
